<!--
  SkuSwapPlacementDecisions -- the Change SKU dialog's "what about this
  placement?" rows (card #545, Jake 2026-10-28).

  One row per saved placement the NEW style does not carry. Each asks for a
  decision BEFORE the swap applies: a location the new style carries (only
  locations no other kept placement will hold), or remove the placement.
  The host (SupplierSkuPanel) holds the decisions and sends them with the SKU
  in one server call; Confirm stays disabled until every row is decided.

  A native <select>, like PlacementLocationSelect, so the choice is a plain
  form control a scenario can drive.
-->
<template>
    <section
        v-if="stranded.length"
        data-testid="sku-swap-placements"
        class="sku-swap-placements"
    >
        <p class="sku-swap-placements__lead">
            The new style doesn't have
            {{ stranded.length === 1 ? "this placement's location" : "these placements' locations" }}.
            Choose a new location, or remove the placement.
        </p>
        <div
            v-for="(p, i) in stranded"
            :key="p.id"
            :data-testid="`sku-swap-placement-${i}`"
            class="sku-swap-placements__row"
        >
            <label
                :for="`sku-swap-placement-${p.id}`"
                class="sku-swap-placements__name"
            >
                <span class="material-icons sku-swap-placements__icon" aria-hidden="true">warning</span>
                {{ labelOf(p) }}
            </label>
            <select
                :id="`sku-swap-placement-${p.id}`"
                :data-testid="`sku-swap-placement-${i}-decision`"
                class="sku-swap-placements__select"
                :value="valueOf(p)"
                @change="onPick(p, $event.target.value)"
            >
                <option value="" disabled>Choose what to do...</option>
                <option
                    v-for="opt in optionsFor(p)"
                    :key="opt.value"
                    :value="opt.value"
                >
                    {{ opt.label }}
                </option>
            </select>
        </div>
    </section>
</template>

<script setup>
import { REMOVE_DECISION, decisionOptions } from "@/utils/skuSwapPlacements";

const props = defineProps({
    /** The placements the new style does not carry. */
    stranded: { type: Array, default: () => [] },
    /** Every saved placement on the product (to keep locations unique). */
    placements: { type: Array, default: () => [] },
    /** The NEW style's live views. */
    views: { type: Array, default: () => [] },
    /** `{ [placementId]: { view_code } | { remove: true } }` */
    modelValue: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:modelValue"]);

function labelOf(p) {
    return p.name || p.placement_zone_name || p.placement_zone_slug || "Placement";
}

function valueOf(p) {
    const d = props.modelValue?.[p.id];
    if (d?.remove) return REMOVE_DECISION;
    return d?.view_code || "";
}

function optionsFor(p) {
    return decisionOptions(p, {
        placements: props.placements,
        views: props.views,
        decisions: props.modelValue,
    });
}

function onPick(p, value) {
    const next = { ...(props.modelValue || {}) };
    if (!value) {
        delete next[p.id];
    } else if (value === REMOVE_DECISION) {
        next[p.id] = { remove: true };
    } else {
        next[p.id] = { view_code: value };
    }
    emit("update:modelValue", next);
}
</script>

<style scoped>
.sku-swap-placements {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid var(--tf-warning, #b25e00);
    border-radius: 6px;
    background: var(--tf-chip-warning-bg, #fff8eb);
}

.sku-swap-placements__lead {
    margin: 0 0 8px;
    font-size: 13px;
}

.sku-swap-placements__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}

.sku-swap-placements__name {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
}

.sku-swap-placements__icon {
    font-size: 18px;
    color: var(--tf-warning, #b25e00);
}

.sku-swap-placements__select {
    width: 100%;
    font-size: 13px;
    padding: 4px 6px;
    border: 1px solid var(--tf-border-default, #d1d5db);
    border-radius: 4px;
    background: var(--tf-bg-surface, #fff);
}
</style>
